import React, { useCallback, useState } from 'react';
import { Stack, ControlLink, Text, Overlay } from '@moda/om';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { RestAddress } from '../../../hooks/useAddress';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useAddressesQuery } from '../../../hooks/useAddress';
import { LoadingBalls } from '../../LoadingBalls';
import { AmazonShippingAddressPreview } from '../AmazonShippingAddressPreview';
import { AmazonPaymentMethodPreview } from '../AmazonPaymentMethodPreview';
import './AmazonShippingAndPaymentPreview.scss';

type Props = {
  address?: RestAddress;
  addressId?: number | null;
  readOnly?: boolean;
  onEdit?: () => void;
  onSwitchToAdyen?: () => Promise<void>;
};

enum Mode {
  Resting,
  Switching
}

export const AmazonShippingAndPaymentPreview: React.FC<Props> = ({
  address,
  addressId,
  readOnly,
  onEdit,
  onSwitchToAdyen
}) => {
  const { showErrorModal } = useErrorModal();
  const { addresses, error: addressesError } = useAddressesQuery({ skip: address != null });
  const [mode, setMode] = useState(Mode.Resting);

  const handleSwitchToAdyen = useCallback(async () => {
    setMode(Mode.Switching);
    try {
      await onSwitchToAdyen?.();
    } catch (error) {
      reportError(error);
      showErrorModal();
    }
    setMode(Mode.Resting);
  }, [setMode, onSwitchToAdyen, showErrorModal]);

  const selectedAddress = address || addresses.find(address => address.id === String(addressId));

  if (addressesError) throwQueryError(addressesError);

  if (!selectedAddress) return null;

  return (
    <>
      <div className="AmazonShippingAndPaymentPreview">
        <div className="AmazonShippingAndPaymentPreview__section">
          <AmazonShippingAddressPreview address={selectedAddress} />
        </div>
        <div className="AmazonShippingAndPaymentPreview__section">
          <AmazonPaymentMethodPreview />
        </div>
        {!readOnly && (
          <Stack
            className="AmazonShippingAndPaymentPreview__actions"
            space={4}
            direction="horizontal"
            justifyContent="flex-end"
          >
            <ControlLink onClick={handleSwitchToAdyen}>
              I do not want to pay with Amazon
            </ControlLink>
            <ControlLink onClick={onEdit}>Edit</ControlLink>
          </Stack>
        )}
      </div>

      <Overlay show={mode === Mode.Switching}>
        <LoadingBalls />
        <Text treatment="eyebrow">Switching</Text>
      </Overlay>
    </>
  );
};
